type WindowWithWebkitAudio = Window &
  typeof globalThis & {
    webkitAudioContext?: typeof AudioContext
  }

let ctx: AudioContext | null = null

function getCtx(): AudioContext | null {
  if (!ctx) {
    const w = window as WindowWithWebkitAudio
    const Ctor = w.AudioContext || w.webkitAudioContext
    if (!Ctor) return null
    ctx = new Ctor()
  }
  if (ctx.state === 'suspended') {
    void ctx.resume()
  }
  return ctx
}

export function useChestSounds() {
  const playPress = () => {
    const c = getCtx()
    if (!c) return
    const now = c.currentTime

    const filter = c.createBiquadFilter()
    filter.type = 'lowpass'
    filter.Q.value = 0.8
    filter.frequency.setValueAtTime(300, now)
    filter.frequency.exponentialRampToValueAtTime(1400, now + 0.05)
    filter.frequency.exponentialRampToValueAtTime(500, now + 0.14)

    const master = c.createGain()
    master.gain.value = 1
    filter.connect(master).connect(c.destination)

    const partial = (
      type: OscillatorType,
      f0: number,
      f1: number,
      peak: number,
      dur: number,
    ) => {
      const osc = c.createOscillator()
      const g = c.createGain()
      osc.type = type
      osc.frequency.setValueAtTime(f0, now)
      osc.frequency.exponentialRampToValueAtTime(f1, now + dur * 0.7)
      g.gain.setValueAtTime(0, now)
      g.gain.linearRampToValueAtTime(peak, now + 0.006)
      g.gain.exponentialRampToValueAtTime(0.0001, now + dur)
      osc.connect(g).connect(filter)
      osc.start(now)
      osc.stop(now + dur + 0.02)
    }

    partial('sine', 220, 130, 0.09, 0.14)
    partial('triangle', 440, 260, 0.035, 0.1)
    partial('sine', 880, 520, 0.015, 0.07)
  }

  const playRelease = () => {
    const c = getCtx()
    if (!c) return
    const now = c.currentTime

    const master = c.createGain()
    master.gain.value = 1
    master.connect(c.destination)

    const noiseBuf = c.createBuffer(
      1,
      Math.floor(c.sampleRate * 0.04),
      c.sampleRate,
    )
    const data = noiseBuf.getChannelData(0)
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1
    const noise = c.createBufferSource()
    noise.buffer = noiseBuf

    const noiseBp = c.createBiquadFilter()
    noiseBp.type = 'bandpass'
    noiseBp.frequency.value = 3200
    noiseBp.Q.value = 1.6

    const noiseGain = c.createGain()
    noiseGain.gain.setValueAtTime(0, now)
    noiseGain.gain.linearRampToValueAtTime(0.05, now + 0.004)
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.04)

    noise.connect(noiseBp).connect(noiseGain).connect(master)
    noise.start(now)
    noise.stop(now + 0.05)

    const bodyFilter = c.createBiquadFilter()
    bodyFilter.type = 'lowpass'
    bodyFilter.Q.value = 0.5
    bodyFilter.frequency.setValueAtTime(2400, now)
    bodyFilter.frequency.exponentialRampToValueAtTime(1400, now + 0.25)
    bodyFilter.connect(master)

    const partial = (
      freq: number,
      peak: number,
      dur: number,
      delay = 0,
      type: OscillatorType = 'sine',
    ) => {
      const osc = c.createOscillator()
      const g = c.createGain()
      osc.type = type
      osc.frequency.setValueAtTime(freq, now + delay)
      g.gain.setValueAtTime(0, now + delay)
      g.gain.linearRampToValueAtTime(peak, now + delay + 0.01)
      g.gain.exponentialRampToValueAtTime(0.0001, now + delay + dur)
      osc.connect(g).connect(bodyFilter)
      osc.start(now + delay)
      osc.stop(now + delay + dur + 0.02)
    }

    partial(523, 0.055, 0.22, 0.005)
    partial(526, 0.04, 0.22, 0.005)
    partial(784, 0.022, 0.2, 0.02)
    partial(1046, 0.009, 0.16, 0.035)
  }

  return { playPress, playRelease }
}
